/**
 * Committee memoranda — the credit paper for one deal.
 *
 * The Committee page used to read memos off the loan payload's `committeeMemo`
 * field, which the loans API does not return; against a live workspace the
 * queue was therefore always empty and only demo fixtures populated it. This
 * reads the memos from the endpoint that owns them.
 */
import api from './api'

export type CommitteeMemoSectionState = {
  /** Sections the deal file actually supplied. */
  sectionsPopulated: string[]
  /** Sections a person still owes. */
  sectionsRequiringManualInput: string[]
}

export type CommitteeMemo = CommitteeMemoSectionState & {
  id: string
  loanId: string
  version: string
  status: string
  committeeDate: string | null
  committeeType: string
  chair: string | null
  attendees: unknown[]
  dealTitle: string | null
  recommendation: string | null
  executiveSummary: string | null
  transactionOverview: Record<string, unknown> | null
  propertyDescription: string | null
  marketAnalysis: string | null
  sponsorAnalysis: string | null
  exitStrategy: string | null
  cashFlowAnalysis: Record<string, unknown> | null
  riskFactors: Array<Record<string, unknown>> | null
  conditionsToApproval: unknown[] | null
  comparableTransactions: unknown[] | null
  generatedBy: string | null
  generatedAt: string | null
  approvedBy: string | null
  approvedAt: string | null
  createdAt: string | null
}

export type AssembleMemoRequest = {
  loanId: string
  committeeDate?: string | null
  committeeType?: string
  chair?: string | null
  attendees?: string[]
  recommendation?: string | null
  conditionsToApproval?: string[]
  riskFactors?: Array<Record<string, unknown>>
}

/** The API speaks snake_case here; keep the mapping in one place. */
function toMemo(raw: Record<string, unknown>): CommitteeMemo {
  const pick = <T>(...keys: string[]): T => {
    for (const key of keys) {
      if (raw[key] !== undefined) return raw[key] as T
    }
    return undefined as unknown as T
  }
  return {
    id: String(raw.id ?? ''),
    loanId: String(pick<string>('loan_id', 'loanId') ?? ''),
    version: String(raw.version ?? 'v1'),
    status: String(raw.status ?? 'Draft'),
    committeeDate: pick<string | null>('committee_date', 'committeeDate') ?? null,
    committeeType: String(pick<string>('committee_type', 'committeeType') ?? 'IC'),
    chair: pick<string | null>('chair') ?? null,
    attendees: (pick<unknown[]>('attendees') as unknown[]) ?? [],
    dealTitle: pick<string | null>('deal_title', 'dealTitle') ?? null,
    recommendation: pick<string | null>('recommendation') ?? null,
    executiveSummary: pick<string | null>('executive_summary', 'executiveSummary') ?? null,
    transactionOverview:
      pick<Record<string, unknown> | null>('transaction_overview', 'transactionOverview') ?? null,
    propertyDescription:
      pick<string | null>('property_description', 'propertyDescription') ?? null,
    marketAnalysis: pick<string | null>('market_analysis', 'marketAnalysis') ?? null,
    sponsorAnalysis: pick<string | null>('sponsor_analysis', 'sponsorAnalysis') ?? null,
    exitStrategy: pick<string | null>('exit_strategy', 'exitStrategy') ?? null,
    cashFlowAnalysis:
      pick<Record<string, unknown> | null>('cash_flow_analysis', 'cashFlowAnalysis') ?? null,
    riskFactors:
      (pick<Array<Record<string, unknown>>>('risk_factors', 'riskFactors') as
        | Array<Record<string, unknown>>
        | null) ?? null,
    conditionsToApproval:
      (pick<unknown[]>('conditions_to_approval', 'conditionsToApproval') as unknown[]) ?? null,
    comparableTransactions:
      (pick<unknown[]>('comparable_transactions', 'comparableTransactions') as unknown[]) ?? null,
    sectionsPopulated:
      (pick<string[]>('sections_populated', 'sectionsPopulated') as string[]) ?? [],
    sectionsRequiringManualInput:
      (pick<string[]>('sections_requiring_manual_input', 'sectionsRequiringManualInput') as
        | string[]) ?? [],
    generatedBy: pick<string | null>('generated_by', 'generatedBy') ?? null,
    generatedAt: pick<string | null>('generated_at', 'generatedAt') ?? null,
    approvedBy: pick<string | null>('approved_by', 'approvedBy') ?? null,
    approvedAt: pick<string | null>('approved_at', 'approvedAt') ?? null,
    createdAt: pick<string | null>('created_at', 'createdAt') ?? null,
  }
}

export const memosService = {
  listCommittee: (loanId?: string) =>
    api
      .get<Record<string, unknown>[]>('/memos/committee', {
        params: loanId ? { loan_id: loanId } : undefined,
      })
      .then((r) => (Array.isArray(r.data) ? r.data.map(toMemo) : [])),

  /**
   * Assemble a memo from what the deal file already holds. The response states
   * which sections came from the record and which a person still owes — the
   * page shows both, because a committee paper that does not say what is
   * missing is worse than a short one.
   */
  assembleCommittee: (body: AssembleMemoRequest) =>
    api
      .post<Record<string, unknown>>('/memos/committee', {
        loan_id: body.loanId,
        committee_date: body.committeeDate ?? null,
        committee_type: body.committeeType ?? 'IC',
        chair: body.chair ?? null,
        attendees: body.attendees ?? [],
        recommendation: body.recommendation ?? null,
        conditions_to_approval: body.conditionsToApproval ?? [],
        risk_factors: body.riskFactors ?? [],
      })
      .then((r) => toMemo(r.data)),
}

/** Section keys as a person reads them. */
export const MEMO_SECTION_LABELS: Record<string, string> = {
  executive_summary: 'Executive Summary',
  transaction_overview: 'Transaction Overview',
  property_description: 'Property Description',
  market_analysis: 'Market Analysis',
  sponsor_analysis: 'Sponsor Analysis',
  exit_strategy: 'Exit Strategy',
  cash_flow_analysis: 'Cash Flow Analysis',
  risk_factors: 'Risk Factors',
  conditions_to_approval: 'Conditions to Approval',
  comparable_transactions: 'Comparable Transactions',
}

export function memoSectionLabel(key: string): string {
  return MEMO_SECTION_LABELS[key] ?? key.replace(/_/g, ' ')
}
